import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';
import moment from 'moment';
import debounce from 'lodash.debounce';

import { InfoCircle } from 'styled-icons/boxicons-solid/InfoCircle';

import { Text, Separator, Button, Row } from '@cuemath/comps';
import { post as assignTeacherPost } from 'actions/student-demo-list';
import { getAll as getTeacherList } from 'actions/teacher-list';
import { openModal, closeModal } from 'actions/modal';
import { DEMO_INELIGIBLE_TEACHERS } from 'constants/api';
import { get as getConfig } from 'actions/configs';
import { EVENT_TYPE } from 'constants/logger';

import {
  ButtonWrapper,
  Head,
  Message,
  FieldWrapper,
  FormWrapper,
  getOptions,
} from 'modules/form';
import { Field, SelectInput, Input } from 'modules/form/index';

import { FORM_ACTIONS, OPTIONS } from 'constants/form';
import { TEACHERS_PER_PAGE } from 'constants/list';
import { trackEvent } from 'modules/analytics';

const BasisLabelContainer = styled(Row)`
  flex-basis: 35%;
`;

const BasisContentContainer = styled(Row)`
  flex-basis: 65%;
`;

const WarningRow = styled(Row)`
  background-color: yellow;
  margin: 5px;
  border: 1px solid;
  padding: 10px;
  border-radius: 5px;
`;

const WarningText = styled(Text)`
  padding-left: 10px;
`;

const InfoCircleIcon = styled(InfoCircle)`
  width: 20px;
  height: 20px;
`;

const MANUAL_TRIAL_REASON_OPTIONS = [
  {
    label: 'Gender Preference: Parent wants specific gender',
    value: 'GENDER_PREFERENCE',
  },
  {
    label: 'Language Preference: Parent wants specific language',
    value: 'LANGUAGE_PREFERENCE',
  },
  { label: 'Sibling case: Parent wants the same teacher', value: 'SIBLING_CASE' },
  { label: 'Parent Referral: Parent has given a lead', value: 'PARENT_REFERRAL' },
  { label: 'Tutor Referral: Tutor has given a lead', value: 'TUTOR_REFERRAL' },
  {
    label: 'Transfers Trial: Parent wants to transfer to a different teacher',
    value: 'TRANSFERS_TRIAL',
  },
  { label: 'Other: "Please mention in detail why manual mapping"', value: 'OTHER' },
];

class AssignTeacherModal extends PureComponent {
  static defaultProps = {
    student: null,
    teacherId: null,
  };

  static propTypes = {
    student: PropTypes.object,
    actions: PropTypes.shape({
      closeModal: PropTypes.func.isRequired,
      getTeacherList: PropTypes.func.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    const { data } = props;

    const existingDate = data.scheduled_start_time
      ? moment(data.scheduled_start_time * 1000).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');
    const existingTime = existingDate
      ? moment(data.scheduled_start_time * 1000).format('HH:mm')
      : moment().format('HH:mm');
    const programId = data && data.program_id;
    const teacherId = data && data.teacherId;

    this.state = {
      student: data,
      page: 1,
      dateError: false,
      startDate: existingDate,
      startTime: existingTime,
      duration: 55,
      errorText: null,
      message: null,
      assignTeacher: store.post(),
      teacherId: teacherId,
      programId: programId,
      teacherSearchKey: null,
      studentId: '',
      manualTrialReason: null,
      manualTrialReasonOther: '',
    };
  }

  componentDidMount() {
    const { actions, teacherId, data } = this.props;
    if (!teacherId) {
      actions.getConfig(DEMO_INELIGIBLE_TEACHERS);
    }
    const { program_id: programId } = data || {};
    if (programId) {
      this.setState({ programId });
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { studentDemoList, teacherList } = props;
    const { assignTeacher, page, teacherSearchKey } = state;

    const newStudentPost = store.post(studentDemoList, assignTeacher.id);

    const newState = {};
    const listParams = { page, per_page: TEACHERS_PER_PAGE, search: teacherSearchKey };

    if (newStudentPost.resolved) {
      if (newStudentPost.resolved.isCreated) {
        newState.assignTeacher = newStudentPost;
        newState.studentData = newStudentPost.resolved.data;
        newState.message =
          newStudentPost.resolved.data?.message || 'Teacher assigned successfully';
        newState.errorText = null;
      } else if (newStudentPost.resolved.isCreatingFailed) {
        newState.assignTeacher = newStudentPost;
        newState.message = null;
        newState.errorText = store.getError(newStudentPost.resolved)
          ? store.getError(newStudentPost.resolved).message
          : 'Error';
      }

      newState.updateStudent = false;
    }
    newState.teachersData = store.getAll(teacherList, listParams);
    newState.studentPost = newStudentPost;

    return newState;
  }

  handlePostAction = () => {
    const { actions, data } = this.props;
    const {
      assignTeacher: { id },
      teacherId,
      startDate,
      startTime,
      duration,
      studentId,
      programId,
    } = this.state;

    const {
      parent_data: parentData,
      teacherId: teacherIdFromProps,
      mappingLogId,
      teacherIdFromAcFlow,
    } = data || {};
    const { parent_country_code: parentCountryCode } = parentData || {};

    const startDateObj = `${startDate} ${startTime}`;
    const startDateTime = moment(startDateObj).format('X');
    const endTime = moment(startDateObj)
      .add(parseInt(duration, 10), 'minutes')
      .format('X');

    if (!programId) {
      this.setState({ errorText: 'Select a program', message: null });
      return;
    }

    // Check if manual trial reason is required for MATH stream programs
    const { programs: programList } = this.props;
    const selectedProgram = programList?.find(program => program.id === programId);
    const { manualTrialReason, manualTrialReasonOther } = this.state;

    if (selectedProgram && selectedProgram.stream === 'MATH') {
      if (!manualTrialReason) {
        this.setState({
          errorText: 'Manual trial scheduling reason is required for MATH programs',
          message: null,
        });
        return;
      }

      if (manualTrialReason === 'OTHER' && !manualTrialReasonOther.trim()) {
        this.setState({
          errorText: 'Please provide details for Other reason',
          message: null,
        });
        return;
      }
    }

    const postData = {};

    postData['student_id'] = studentId || data.id;
    postData['teacher_id'] = teacherIdFromAcFlow || teacherId;
    postData['program_id'] = programId;
    postData['scheduled_start_time'] = parseInt(startDateTime, 10);
    postData['scheduled_end_time'] = parseInt(endTime, 10);
    postData['is_plus_lead'] = data.is_plus_lead;
    postData['parent_country_code'] = parentCountryCode;

    if (mappingLogId) {
      postData['mapping_log_id'] = mappingLogId;
    }

    // Add manual trial reason for MATH programs
    if (selectedProgram && selectedProgram.stream === 'MATH' && manualTrialReason) {
      postData['manual_trial_reason'] = manualTrialReason;
      if (manualTrialReason === 'OTHER' && manualTrialReasonOther.trim()) {
        postData['manual_trial_reason'] = manualTrialReasonOther.trim();
      }
    }

    actions.assignTeacherPost(id, postData, { studentId: studentId });

    const eventType = teacherIdFromProps
      ? EVENT_TYPE.TEACHER_MAPPING_DEMO_ASSIGN_CLICKED
      : EVENT_TYPE.MANUAL_DEMO_ASSIGN_CLICKED;
    trackEvent(eventType, postData);
  };

  onTextChange = (val, reason) => {
    const { action } = reason;
    const { actions } = this.props;
    const { page, errorText, studentId, student = {} } = this.state;
    const listParams = { page, per_page: TEACHERS_PER_PAGE, search: val };

    const resolvedStudentId = student['id'] || studentId;
    const filterData = {
      page,
      per_page: TEACHERS_PER_PAGE,
      state: ['ACT', 'INTR', 'FDTR'],
      search: val,
      only_demo_eligible: false,
      student_id: resolvedStudentId,
    };

    if (val.length <= 4 && action !== 'menu-close') {
      this.setState({ errorText: 'Please enter more than 4 characters', message: null });
      return;
    }
    if (errorText) {
      this.setState({ errorText: null });
    }

    if (action !== 'menu-close' || val.length !== 0) {
      this.setState({ teacherSearchKey: val }, () => {
        actions.getTeacherList(listParams, filterData);
      });
    }
  };

  onSubmit = event => {
    const {
      assignTeacher: { current },
      studentPost: { current: studentPostCurrent },
    } = this.state;
    const { formType } = this.props;
    event.preventDefault();

    if (studentPostCurrent && studentPostCurrent.isCreating) return true;
    if (formType === FORM_ACTIONS.CREATE && !(current && current.isCreating))
      this.handlePostAction();
  };

  renderFormFields = () => {
    const { teachersData } = this.state;
    const { excludedTeachersList } = this.props;
    const { isLoaded, data: excludedTeachers } = excludedTeachersList || {};

    let teacherOptions = [];
    if (
      teachersData &&
      teachersData.data &&
      Array.isArray(teachersData.data) &&
      teachersData.data.length > 0 &&
      isLoaded &&
      excludedTeachers &&
      Array.isArray(excludedTeachers)
    ) {
      teacherOptions = getOptions({
        field: OPTIONS.TEACHERS_LIST,
        teachersDataOptions: teachersData.data.map(teacher => {
          return teacher;
        }),
      });
    }

    return (
      <Field key="region">
        <Text size="normal">Choose a teacher</Text>
        <BasisContentContainer>
          <SelectInput
            name="teacher"
            options={teacherOptions}
            onInputChange={debounce(
              (val, reason) => this.onTextChange(val, reason),
              1000,
            )}
            onChange={this.onTeacherSelect}
            isOptionDisabled={option => option.disabled}
            isMulti={false}
          />
          <Text size="small">* greyed out teachers are not eligible for demo</Text>
        </BasisContentContainer>
      </Field>
    );
  };

  renderProgramsField = () => {
    const { programs: programList } = this.props;
    const { programId } = this.state;
    const programs = programList?.filter(
      program => program.is_live && program.board === 'DEMO',
    );
    const programChoices = getOptions({ field: 'program', programs });
    const selectedProgram = programList.find(program => program.id === programId);

    return (
      <Field key="program">
        <Text size="normal">Program</Text>
        <BasisContentContainer>
          <SelectInput
            name="program"
            options={programChoices}
            defaultValue={
              selectedProgram
                ? { label: selectedProgram.code, value: selectedProgram.id }
                : undefined
            }
            onChange={this.onProgramSelect}
            isMulti={false}
          />
        </BasisContentContainer>
      </Field>
    );
  };

  renderManualTrialReasonField = () => {
    const { programs: programList } = this.props;
    const { programId, manualTrialReason, manualTrialReasonOther } = this.state;

    // Only show this field for MATH stream programs
    const selectedProgram = programList?.find(program => program.id === programId);
    if (!selectedProgram || selectedProgram.stream !== 'MATH') {
      return null;
    }

    const selectedReasonOption = MANUAL_TRIAL_REASON_OPTIONS.find(
      option => option.value === manualTrialReason,
    );

    return (
      <>
        <Field key="manual_trial_reason">
          <Text size="normal">Manual Trial Scheduling Reason *</Text>
          <BasisContentContainer>
            <SelectInput
              name="manual_trial_reason"
              options={MANUAL_TRIAL_REASON_OPTIONS}
              value={selectedReasonOption}
              onChange={this.onManualTrialReasonSelect}
              isMulti={false}
              placeholder="Select a reason"
            />
          </BasisContentContainer>
        </Field>
        {manualTrialReason === 'OTHER' && (
          <Field key="manual_trial_reason_other">
            <Text size="normal">Please mention in detail why manual mapping *</Text>
            <BasisContentContainer>
              <Input
                name="manual_trial_reason_other"
                type="text"
                required={true}
                value={manualTrialReasonOther}
                onChange={this.onManualTrialReasonOtherChange}
                placeholder="Provide detailed reason"
              />
            </BasisContentContainer>
          </Field>
        )}
      </>
    );
  };

  onChange = event => {
    this.setState({
      dateError: false,
      startDate: event,
    });
  };

  onAddStudentId = event => {
    this.setState({
      studentId: event.value,
    });
  };

  onTimeChange = event => {
    this.setState({
      dateError: false,
      startTime: event,
    });
  };

  onDurationChange = event => {
    this.setState({
      dateError: false,
      duration: event,
    });
  };

  onTeacherSelect = teacher => {
    this.setState({ teacherId: teacher.value });
  };

  onProgramSelect = program => {
    this.setState({
      programId: program.value,
      // Reset manual trial reason when program changes
      manualTrialReason: null,
      manualTrialReasonOther: '',
    });
  };

  onManualTrialReasonSelect = reason => {
    this.setState({
      manualTrialReason: reason.value,
      manualTrialReasonOther: '',
    });
  };

  onManualTrialReasonOtherChange = value => {
    this.setState({
      manualTrialReasonOther: value,
    });
  };

  render() {
    const {
      errorText,
      startTime,
      startDate,
      error,
      message,
      duration,
      studentId,
      studentPost,
    } = this.state;
    const currentDate = moment().format('YYYY-MM-DD');

    const { data, programs } = this.props;
    const { current: studentPostCurrent } = studentPost || {};

    return (
      <>
        <form onSubmit={this.onSubmit}>
          <FormWrapper>
            <Head>
              <Text size="xlarge" align="center">
                {data && data.teacherId && data.teacherName
                  ? `Assign demo to ${data.teacherName}`
                  : 'Assign a teacher'}
              </Text>
            </Head>
            <WarningRow>
              <InfoCircleIcon />
              <WarningText>
                Note: This Demo Class will be conducted using 3-Way call(AC call).
              </WarningText>
            </WarningRow>
            <Separator transparent height="50" />
            <FieldWrapper>
              {!(data && data.teacherId) && data?.id && this.renderFormFields()}
              {!data?.id && (
                <Field key="student_id">
                  <BasisLabelContainer>
                    <Text size="normal">Student Id</Text>
                  </BasisLabelContainer>
                  <BasisContentContainer>
                    <Input
                      name="studentId"
                      required={true}
                      value={studentId}
                      onChange={this.onAddStudentId}
                    />
                  </BasisContentContainer>
                </Field>
              )}
              <Field key="demo_date">
                <BasisLabelContainer>
                  <Text size="normal">Demo date</Text>
                </BasisLabelContainer>
                <BasisContentContainer>
                  <Input
                    name="startDate"
                    type="date"
                    required={true}
                    value={startDate}
                    min={currentDate}
                    onChange={this.onChange}
                    error={error}
                  />
                </BasisContentContainer>
              </Field>
              <Field key="demo_time">
                <BasisLabelContainer>
                  <Text size="normal">Demo time</Text>
                </BasisLabelContainer>
                <BasisContentContainer>
                  <Input
                    name="startTime"
                    type="time"
                    required={true}
                    value={startTime}
                    onChange={this.onTimeChange}
                  />
                </BasisContentContainer>
              </Field>
              <Field key="duration">
                <BasisLabelContainer>
                  <Text size="normal">Duration</Text>{' '}
                </BasisLabelContainer>
                <BasisContentContainer>
                  <Input
                    name="duration"
                    type="text"
                    placeholder="Minutes"
                    disabled={true}
                    value={duration}
                    onChange={this.onDurationChange}
                  />
                </BasisContentContainer>
              </Field>
              {programs && this.renderProgramsField()}
              {programs && this.renderManualTrialReasonField()}
              <Separator transparent height="20" />
              {errorText && <Message error={true}>{errorText}</Message>}
              {message && <Message>{message}</Message>}
              <Separator transparent height="50" />
              <ButtonWrapper>
                <Button
                  label="Confirm"
                  id="submit"
                  busy={studentPostCurrent && studentPostCurrent.isCreating}
                />
              </ButtonWrapper>
              <Separator transparent height="50" />
            </FieldWrapper>
          </FormWrapper>
        </form>
      </>
    );
  }
}

export default connect(
  ({ studentDemoList, teacherList, student, configs }) => {
    return {
      studentDemoList,
      teacherList,
      student,
      excludedTeachersList: store.get(configs, DEMO_INELIGIBLE_TEACHERS),
    };
  },
  dispatch => ({
    actions: bindActionCreators(
      {
        openModal,
        assignTeacherPost,
        closeModal,
        getConfig,
        getTeacherList,
      },
      dispatch,
    ),
  }),
)(AssignTeacherModal);
